import React from 'react';
import { Star, Check } from 'lucide-react';
import FishboneDiagram from './FishboneDiagram';
import { TONES, SheetIcon } from './sheetStyle';

const inputCls = 'w-full bg-transparent border-0 border-b border-[#cdbfd0] focus:border-[#6b3a5e] focus:ring-0 focus:outline-none font-body text-[15px] text-stone-800 py-1.5 placeholder:text-stone-300';

const Tick = ({ on, onClick, testId }) => (
  <button type="button" onClick={onClick} className={`w-5 h-5 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${on ? 'bg-[#6b3a5e] border-[#6b3a5e] text-white' : 'border-[#b9a8bd] hover:border-[#6b3a5e]'}`} data-testid={testId}>
    {on && <Check size={13} strokeWidth={3} />}
  </button>
);

const Label = ({ children, hint }) => (!children && !hint ? null : (
  <div className="mb-3">
    {children && <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#6b3a5e]">{children}</p>}
    {hint && <p className="font-body text-sm text-stone-500 mt-0.5">{hint}</p>}
  </div>
));

const rowsOf = (v, n) => Array.from({ length: n }, (_, i) => (v && v[i]) || {});

const TextField = ({ f, v, set }) => (
  <div>
    <Label hint={f.hint}>{f.label}</Label>
    <input value={v || ''} onChange={(e) => set(e.target.value)} className={inputCls} placeholder="Write here…" data-testid={`fb-field-${f.id}`} />
  </div>
);

const LinesField = ({ f, v, set }) => {
  const rows = rowsOf(v, f.rows);
  const upd = (i, patch) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <Label hint={f.star ? f.starHint : f.hint}>{f.label}</Label>
      <div className={f.twoCol ? 'grid sm:grid-cols-2 gap-x-8 gap-y-0.5' : 'space-y-0.5'}>
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={r.text || ''} onChange={(e) => upd(i, { text: e.target.value })} className={`${inputCls} ${r.star ? 'font-semibold text-[#6b3a5e]' : ''}`} data-testid={`fb-field-${f.id}-${i}`} />
            {f.star && (
              <button type="button" onClick={() => upd(i, { star: !r.star })} className={`p-1 transition-colors ${r.star ? 'text-amber-500' : 'text-stone-300 hover:text-amber-400'}`} aria-label="Circle this" data-testid={`fb-star-${f.id}-${i}`}>
                <Star size={16} className={r.star ? 'fill-current' : ''} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const RUNG_BG = ['#f6f0f5', '#eef2e8', '#e8f1f5', '#f8efe6', '#f3ecf2'];
const ChecklistRows = ({ id, rows, labels, set, rungs }) => {
  const upd = (i, patch) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={i} className={`flex items-center gap-3 ${rungs ? 'rounded-xl px-3 py-2 border border-white shadow-sm' : ''}`} style={rungs ? { background: RUNG_BG[i % RUNG_BG.length] } : undefined}>
          {labels?.[i] && <span className={`font-body text-xs font-semibold uppercase tracking-wider text-stone-500 flex-shrink-0 ${labels[i].length > 2 ? 'w-14' : 'w-5'}`}>{labels[i]}</span>}
          <Tick on={!!r.done} onClick={() => upd(i, { done: !r.done })} testId={`fb-tick-${id}-${i}`} />
          <input value={r.text || ''} onChange={(e) => upd(i, { text: e.target.value })} className={`${inputCls} ${r.done ? 'line-through text-stone-400' : ''}`} data-testid={`fb-field-${id}-${i}`} />
        </div>
      ))}
    </div>
  );
};

const ChecklistField = ({ f, v, set }) => (
  <div>
    <Label hint={f.hint}>{f.label}</Label>
    <ChecklistRows id={f.id} rows={rowsOf(v, f.labels.length)} labels={f.labels} set={set} rungs={f.rungs} />
  </div>
);

const OptionsField = ({ f, v, set }) => {
  // multi: v = [indexes]; single: v = { pick, other }
  const single = !!f.single;
  const on = single ? (v?.pick ?? null) : (Array.isArray(v) ? v : []);
  const isOn = (i) => (single ? on === i : on.includes(i));
  const toggle = (i) => (single
    ? set({ ...(v || {}), pick: on === i ? null : i, other: '' })
    : set(on.includes(i) ? on.filter((x) => x !== i) : [...on, i]));
  return (
    <div>
      <Label hint={f.hint}>{f.label}</Label>
      <div className={f.inline ? 'flex flex-wrap gap-x-6 gap-y-2' : f.optCols === 2 ? 'grid sm:grid-cols-2 gap-x-6 gap-y-2' : 'space-y-2'}>
        {f.options.map((o, i) => (
          <label key={o} className="flex items-center gap-3 cursor-pointer">
            <Tick on={isOn(i)} onClick={() => toggle(i)} testId={`fb-option-${f.id}-${i}`} />
            <span className="font-body text-[15px] text-stone-700">{o}</span>
          </label>
        ))}
      </div>
      {f.other && (
        <div className="flex items-center gap-3 mt-2">
          <span className="font-body text-[15px] text-stone-700">Other:</span>
          <input value={v?.other || ''} onChange={(e) => set({ ...(v || {}), other: e.target.value, pick: e.target.value ? 'other' : null })} className={inputCls} data-testid={`fb-option-${f.id}-other`} />
        </div>
      )}
    </div>
  );
};

const QuoteField = ({ f }) => (
  <div className="rounded-2xl bg-[#f3ecf2] px-6 py-5 text-center" data-testid={`fb-quote-${f.id}`}>
    {f.label && <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#6b3a5e] mb-2">{f.label}</p>}
    {f.lines.map((l) => <p key={l} className={`font-heading text-[#6b3a5e] ${f.bullets ? 'text-base text-left' : 'text-xl italic'}`}>{f.bullets ? `• ${l}` : l}</p>)}
  </div>
);

const PairsField = ({ f, v, set }) => {
  const rows = rowsOf(v, f.items.length);
  const upd = (i, patch) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <Label hint={f.hint}>{f.label}</Label>
      <div className="space-y-2">
        {f.items.map((it, i) => (
          <div key={it.title} className={`grid sm:grid-cols-2 gap-2 rounded-xl p-3 transition-colors ${rows[i].done ? 'bg-[#efe6ee]' : 'bg-white border border-stone-100'}`} data-testid={`fb-pair-${f.id}-${i}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <Tick on={!!rows[i].done} onClick={() => upd(i, { done: !rows[i].done })} testId={`fb-tick-${f.id}-${i}`} />
              <span>
                <span className="block font-body text-sm font-semibold uppercase tracking-wider text-[#6b3a5e]">{it.title}</span>
                <span className="block font-body text-sm italic text-stone-500">{it.sub}</span>
              </span>
            </label>
            {it.help ? (
              <p className={`font-body text-sm text-stone-700 ${rows[i].done ? '' : 'opacity-60'}`}><strong>{it.help[0]}</strong> {it.help[1]}</p>
            ) : (
              <input value={rows[i].text || ''} onChange={(e) => upd(i, { text: e.target.value })} placeholder="What might help…" className={inputCls} data-testid={`fb-field-${f.id}-${i}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const GroupsField = ({ f, v, set }) => {
  const count = f.titles ? f.titles.length : f.count;
  const groups = Array.from({ length: count }, (_, i) => (v && v[i]) || {});
  const upd = (i, patch) => set(groups.map((g, j) => (j === i ? { ...g, ...patch } : g)));
  return (
    <div className={`grid sm:grid-cols-2 ${count === 3 ? 'lg:grid-cols-3' : ''} gap-4`}>
      {groups.map((g, i) => {
        const t = f.titles?.[i] || {};
        const tone = t.tone ? TONES[t.tone] : null;
        return (
          <div key={i} className={`${tone ? 'rounded-3xl p-5' : 'rounded-2xl bg-[#f7f2f6] border border-[#e6dbe4] p-4'}`} style={tone ? { background: tone.bg } : undefined} data-testid={`fb-group-${f.id}-${i}`}>
            {f.titles ? (
              <div className="mb-3 flex items-start gap-3">
                {t.icon && <SheetIcon name={t.icon} color={tone?.ink} size={44} />}
                {t.num && <span className="w-9 h-9 min-w-[2.25rem] rounded-full text-white font-heading text-lg flex items-center justify-center" style={{ background: '#6b3a5e' }}>{t.num}</span>}
                <div className={t.icon ? 'text-center flex-1' : ''}>
                  <p className={`leading-none ${tone ? 'font-body text-2xl font-extrabold uppercase tracking-wide' : 'font-heading text-2xl'}`} style={{ color: tone ? tone.ink : '#6b3a5e' }}>{t.title}</p>
                  <p className="font-body text-sm text-stone-600 mt-1">{t.sub}</p>
                </div>
              </div>
            ) : (
              <input value={g.title || ''} onChange={(e) => upd(i, { title: e.target.value })} placeholder={`${f.titlePlaceholder} ${i + 1}`} className={`${inputCls} font-semibold uppercase tracking-wider text-sm mb-1`} data-testid={`fb-group-title-${f.id}-${i}`} />
            )}
            <div className={tone ? 'bg-white/90 rounded-2xl p-3' : ''}>
              {f.checkbox ? (
                <ChecklistRows id={`${f.id}-${i}`} rows={rowsOf(g.rows, f.rows)} set={(rows) => upd(i, { rows })} />
              ) : (
                <LinesField f={{ id: `${f.id}-${i}`, rows: f.rows }} v={g.rows} set={(rows) => upd(i, { rows })} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const LabeledField = ({ f, v, set }) => {
  const val = v || {};
  return (
    <div>
      <Label hint={f.hint}>{f.label}</Label>
      <div className="space-y-2">
        {f.items.map((it, i) => (
          <div key={it} className="flex items-center gap-3">
            {f.tick && <Tick on={!!val[`t${i}`]} onClick={() => set({ ...val, [`t${i}`]: !val[`t${i}`] })} testId={`fb-tick-${f.id}-${i}`} />}
            <span className="font-body text-sm font-semibold text-[#1f5068] bg-[#e3eef2] rounded-full px-3 py-1 whitespace-nowrap sm:w-60 sm:text-center flex-shrink-0">{it}</span>
            <input value={val[i] || ''} onChange={(e) => set({ ...val, [i]: e.target.value })} className={inputCls} data-testid={`fb-field-${f.id}-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

const numCls = 'w-16 text-center bg-transparent border-0 border-b border-[#cdbfd0] focus:border-[#6b3a5e] focus:ring-0 focus:outline-none font-body text-[15px] py-1';

const HoursField = ({ f, v, set }) => {
  const rows = rowsOf(v, f.items.length);
  const upd = (i, patch) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const used = rows.reduce((s, r) => s + (parseFloat(r.hrs) || 0), 0);
  const left = Math.round((24 - used) * 10) / 10;
  return (
    <div>
      <Label hint={f.hint}>{f.label}</Label>
      <div className="space-y-2">
        {f.items.map((it, i) => (
          <div key={it} className="flex items-center gap-3">
            <Tick on={!!rows[i].done} onClick={() => upd(i, { done: !rows[i].done })} testId={`fb-tick-${f.id}-${i}`} />
            <span className="flex-1 font-body text-[15px] text-stone-700">{it}</span>
            <input type="number" min="0" step="0.5" inputMode="decimal" value={rows[i].hrs || ''} onChange={(e) => upd(i, { hrs: e.target.value })} className={numCls} data-testid={`fb-hours-${f.id}-${i}`} />
            <span className="font-body text-sm text-stone-500 w-6">hrs</span>
          </div>
        ))}
      </div>
      <p className="mt-4 rounded-full bg-[#e8eee3] px-4 py-2 font-body text-sm text-[#3f5e2f]" data-testid={`fb-hours-left-${f.id}`}>
        Available time for my list: <strong>{used ? `${left} hrs` : '—'}</strong>{used ? ' (out of 24)' : ''}
      </p>
    </div>
  );
};

const TableField = ({ f, v, set }) => {
  const rows = rowsOf(v, f.rows);
  const upd = (i, patch) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <Label hint={f.hint}>{f.label}</Label>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-x-3 gap-y-1 py-1 border-b border-stone-100 last:border-0" data-testid={`fb-row-${f.id}-${i}`}>
            <Tick on={!!r.done} onClick={() => upd(i, { done: !r.done })} testId={`fb-tick-${f.id}-${i}`} />
            <input value={r.text || ''} onChange={(e) => upd(i, { text: e.target.value })} placeholder="Task" className={`${inputCls} flex-1 min-w-[8rem] ${r.done ? 'line-through text-stone-400' : ''}`} data-testid={`fb-field-${f.id}-${i}`} />
            <span className="flex items-center gap-1">
              <input type="number" min="0" inputMode="numeric" value={r.min || ''} onChange={(e) => upd(i, { min: e.target.value })} className={numCls} data-testid={`fb-min-${f.id}-${i}`} />
              <span className="font-body text-sm text-stone-500">min</span>
            </span>
            <span className="flex gap-1">
              {f.priorities.map((p) => (
                <button key={p} type="button" onClick={() => upd(i, { p: r.p === p ? null : p })} className={`px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${r.p === p ? 'bg-[#6b3a5e] text-white border-[#6b3a5e]' : 'border-stone-200 text-stone-500 hover:border-[#6b3a5e]'}`} data-testid={`fb-prio-${f.id}-${i}-${p.replace(/\s/g, '')}`}>{p}</button>
              ))}
            </span>
          </div>
        ))}
      </div>
      {f.totalLabel && (
        <p className="mt-3 font-body text-sm text-stone-600" data-testid={`fb-total-${f.id}`}>
          {f.totalLabel}: <strong>{rows.reduce((s, r) => s + (parseFloat(r.min) || 0), 0)} min</strong>
        </p>
      )}
    </div>
  );
};

const BlocksField = ({ f, v, set }) => {
  const rows = rowsOf(v, f.rows);
  const upd = (i, patch) => set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div>
      <Label hint={f.hint}>{f.label}</Label>
      <div className="rounded-xl border border-[#d9cbd6] overflow-hidden">
        <div className="grid grid-cols-[6.5rem_1fr] bg-[#eef0e8] font-body text-xs font-semibold uppercase tracking-wider text-stone-600">
          <span className="px-3 py-2 border-r border-[#d9cbd6]">Time</span><span className="px-3 py-2">Focus / Activity</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className={`grid grid-cols-[6.5rem_1fr] border-t border-[#e6dbe4] ${i % 2 ? 'bg-[#faf7f9]' : 'bg-white'}`}>
            <input value={r.time || ''} onChange={(e) => upd(i, { time: e.target.value })} placeholder="9:00" className="px-3 py-2 bg-transparent border-r border-[#e6dbe4] focus:outline-none font-body text-[15px]" data-testid={`fb-block-time-${f.id}-${i}`} />
            <input value={r.text || ''} onChange={(e) => upd(i, { text: e.target.value })} className="px-3 py-2 bg-transparent focus:outline-none font-body text-[15px]" data-testid={`fb-block-text-${f.id}-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export const TYPES = { text: TextField, lines: LinesField, checklist: ChecklistField, options: OptionsField, groups: GroupsField, quote: QuoteField, pairs: PairsField, labeled: LabeledField, hours: HoursField, table: TableField, blocks: BlocksField };

export default function SheetFields({ sheet, data, onChange }) {
  return (
    <div className="space-y-8">
      {sheet.id === 'fishbone' && <FishboneDiagram data={data} />}
      {sheet.fields.map((f) => {
        const Field = TYPES[f.type];
        return (
          <section key={f.id} className={f.id === 'next' ? 'rounded-2xl bg-[#eef0e8] p-5' : f.boxed ? 'rounded-2xl bg-white border border-stone-200 p-5' : ''}>
            <Field f={f} v={data[f.id]} set={(val) => onChange({ ...data, [f.id]: val })} />
          </section>
        );
      })}
    </div>
  );
}
